import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { settle, arcPoint } from "./anim";

/**
 * Anivaze v4 — generated-image actors. Two DESIGNS, rendered differently:
 *  - ScenePlate: a full illustration with a slow Ken-Burns camera (zoom+drift),
 *    rounded corners and a hairline theme border.
 *  - StickerCutout: a single prop presented as a die-cut sticker card — circular
 *    mask, THICK theme-colored border, gentle float — visually distinct from a
 *    scene, as specified.
 * Images arrive as data URIs (assetsClient) → deterministic, export-safe.
 */

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Placeholder while the asset is being generated/fetched. */
export const AssetPending: React.FC<{ box: Box; theme: Theme; unit: number; t: number; label: string }> = ({ box, theme, unit, t, label }) => (
  <g opacity={0.5 + 0.2 * Math.sin(t * 3)}>
    <rect x={box.cx - box.w * 0.35} y={box.cy - box.h * 0.28} width={box.w * 0.7} height={box.h * 0.56}
      rx={unit * 2} fill="none" stroke={theme.palette.muted} strokeWidth={2} strokeDasharray="8 8" />
    <text x={box.cx} y={box.cy} textAnchor="middle" dominantBaseline="central"
      fontFamily={theme.body.family} fontWeight={600} fontSize={unit * 2.2} fill={theme.palette.muted}>
      {label}
    </text>
  </g>
);

/** Full illustration with a deterministic Ken-Burns camera. */
export const ScenePlate: React.FC<{ href: string; box: Box; theme: Theme; unit: number; t: number; idSeed: string }> = ({
  href, box, theme, unit, t, idSeed,
}) => {
  const w = Math.min(box.w, box.h * 0.9) * 0.96;
  const h = w; // generated images are square
  const x = box.cx - w / 2;
  const y = box.cy - h / 2;
  // Cinematic still (anim.ts): spring-settled entrance, then a documentary
  // camera — the drift travels along an ARC (never a straight/mechanical line),
  // with a slow push-in and a barely-there roll. Reads as a filmed plate.
  const appear = settle(t, 0.6, 0.15);
  const zoom = 1.07 + 0.07 * (0.5 - 0.5 * Math.cos(t * 0.2));
  const p = 0.5 - 0.5 * Math.cos(t * 0.13); // 0→1→0 slow loop along the arc
  const drift = arcPoint(-w * 0.016, -h * 0.01, w * 0.016, h * 0.012, p, 0.35);
  const roll = Math.sin(t * 0.09) * 0.35; // degrees — subliminal handheld life
  const clip = `scene-${idSeed}`;
  return (
    <g opacity={Math.min(1, appear)}>
      <defs>
        <clipPath id={clip}>
          <rect x={x} y={y} width={w} height={h} rx={unit * 2.2} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        <image
          href={href} x={x} y={y} width={w} height={h}
          preserveAspectRatio="xMidYMid slice"
          transform={`translate(${box.cx + drift.x} ${box.cy + drift.y}) scale(${zoom}) rotate(${roll.toFixed(3)}) translate(${-box.cx} ${-box.cy})`}
        />
      </g>
      <rect x={x} y={y} width={w} height={h} rx={unit * 2.2} fill="none"
        stroke={theme.palette.primary} strokeOpacity={0.45} strokeWidth={1.5} />
    </g>
  );
};

/** A prop as a die-cut sticker: circular mask + thick border + gentle float. */
export const StickerCutout: React.FC<{ href: string; box: Box; theme: Theme; unit: number; t: number; idSeed: string; label?: string }> = ({
  href, box, theme, unit, t, idSeed, label,
}) => {
  const r = Math.min(box.w, box.h) * 0.32;
  const appear = clamp01(t / 0.5);
  const pop = lerp(0.7, 1, 1 - Math.pow(1 - appear, 3));
  const cy = box.cy + Math.sin(t * 0.9) * unit * 0.8; // float
  const tilt = Math.sin(t * 0.5) * 2.5;
  const clip = `stk-${idSeed}`;
  return (
    <g opacity={appear} transform={`rotate(${tilt} ${box.cx} ${cy}) translate(${box.cx} ${cy}) scale(${pop}) translate(${-box.cx} ${-cy})`}>
      <defs>
        <clipPath id={clip}>
          <circle cx={box.cx} cy={cy} r={r} />
        </clipPath>
      </defs>
      {/* sticker body (the image's clean white bg becomes the sticker paper) */}
      <circle cx={box.cx} cy={cy} r={r + unit * 1.4} fill={theme.dark ? "#f2f0ea" : "#ffffff"} opacity={0.95} />
      <g clipPath={`url(#${clip})`}>
        <image href={href} x={box.cx - r} y={cy - r} width={r * 2} height={r * 2} preserveAspectRatio="xMidYMid slice" />
      </g>
      {/* THICK border — the sticker signature */}
      <circle cx={box.cx} cy={cy} r={r + unit * 1.4} fill="none" stroke={theme.palette.primary} strokeWidth={unit * 0.9} />
      {label && (
        <text x={box.cx} y={cy + r + unit * 4.6} textAnchor="middle"
          fontFamily={theme.body.family} fontWeight={700} fontSize={unit * 2.3}
          fill={theme.palette.ink} opacity={0.85}>{label}</text>
      )}
    </g>
  );
};
